/** Ограниченное множество для синхронной дедупликации до вызова побочных эффектов. */
export class BoundedSeenSet<T> {
  private readonly values = new Set<T>();
  private readonly capacity: number;

  constructor(capacity = 2000) {
    if (capacity < 1) throw new Error('capacity must be positive');
    this.capacity = capacity;
  }

  has(value: T): boolean {
    return this.values.has(value);
  }

  add(value: T): void {
    this.values.add(value);
    if (this.values.size > this.capacity) {
      const oldest = this.values.values().next().value;
      if (oldest !== undefined) this.values.delete(oldest);
    }
  }

  delete(value: T): void {
    this.values.delete(value);
  }

  clear(): void {
    this.values.clear();
  }
}

/** Cursor подтверждается только после успешного завершения обработки страницы. */
export function applyBeforeCursor(current: number, next: number, apply: () => void): number {
  apply();
  return Math.max(current, next);
}

/** Изолировать ошибку одного события, чтобы poison event не блокировал всю страницу /sync. */
export function applyEventsIndividually<T>(
  events: readonly T[],
  apply: (event: T) => void,
  onError: (event: T, error: unknown) => void,
): void {
  for (const event of events) {
    try {
      apply(event);
    } catch (error) {
      try {
        onError(event, error);
      } catch {
        /* logging must not let one event stop the rest of the page */
      }
    }
  }
}

/** Задержка повтора /sync: 1, 2, 4, 8, 16, 30 секунд с верхним пределом. */
export function syncRetryDelayMs(failures: number, baseMs = 1_000): number {
  const attempt = Math.min(Math.max(0, Math.floor(failures)), 5);
  return Math.min(30_000, baseMs * 2 ** attempt);
}
